import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ISFATHENA_BASE, GROWTHOS_BASE } from '../config/api';

// Base URLs now come from src/config/api.js — one place for the whole app.
const API_BASE_URL = ISFATHENA_BASE;   // /isfathena/... (session ping/login/logout)
const COMPANY_API_BASE = GROWTHOS_BASE; // /growthos/... (company auth/login)

// ─── Session heartbeat (ping every 60s so admin sees user as active) ───────────
function startSessionHeartbeat(email) {
  if (window._sessionInterval) clearInterval(window._sessionInterval);

  const ping = () => {
    fetch(`${API_BASE_URL}/session/ping`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ email }),
    }).catch(() => {}); // silent fail — don't break the UI
  };

  ping(); // immediate first ping
  window._sessionInterval = setInterval(ping, 60000); // every 60 seconds
}

function Login() {
  const navigate = useNavigate();
  const [loginType, setLoginType] = useState('user'); // 'user' | 'company'
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');

  useEffect(() => {
    // Disable right-click
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable certain keyboard shortcuts
    const handleKeyDown = (e) => {
      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'J')) ||
        (e.ctrlKey && e.key === 'U')
      ) {
        e.preventDefault();
        return false;
      }
    };

    // Disable text selection and copy
    const handleCopy = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable drag
    const handleDragStart = (e) => {
      e.preventDefault();
      return false;
    };

    // Add event listeners
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('dragstart', handleDragStart);

    // Disable text selection via CSS
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    document.body.style.msUserSelect = 'none';

    // Cleanup
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('dragstart', handleDragStart);
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
      document.body.style.msUserSelect = '';
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (apiError) {
      setApiError('');
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 1) {
      newErrors.password = 'Password is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setApiError('');

    // ── Company Login (GrowthOS backend) ───────────────────────────────────────
    if (loginType === 'company') {
      try {
        const response = await fetch(`${COMPANY_API_BASE}/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: formData.email,
            password: formData.password,
          }),
        });
        const data = await response.json();

        if (data.success) {
          if (data.token) {
            localStorage.setItem('authToken', data.token);
            localStorage.setItem('growthos_token', data.token);
          }
          if (data.company) {
            localStorage.setItem('userData', JSON.stringify(data.company));
            localStorage.setItem('growthos_company', JSON.stringify(data.company));
          }
          // Clear any leftover team-member identity from a previous
          // session — otherwise company_Dashboard_sidebar.js will keep
          // showing that stale member's name/email instead of whoever
          // just logged in now. If THIS login response includes its own
          // `member` field (e.g. a team-member login), store that fresh
          // one right after.
          localStorage.removeItem('growthos_member');
          if (data.member) {
            localStorage.setItem('growthos_member', JSON.stringify(data.member));
          }
          localStorage.setItem('userType', 'company');
          localStorage.setItem('sessionEmail', formData.email);

          if (formData.rememberMe) {
            localStorage.setItem('rememberMe', 'true');
            localStorage.setItem('userEmail', formData.email);
          }

          window.dispatchEvent(new Event('authChange'));
          navigate('/company_home');
        } else {
          setApiError(data.message || 'Invalid company email or password');
        }
      } catch (err) {
        setApiError('Could not reach the server. Please try again.');
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // ── Static Super Admin Login ──────────────────────────────────────────────
    if (formData.email === 'info@easylearn.net.in' && formData.password === '1') {
      console.log('Super Admin Login - Static Credentials');
      
      localStorage.setItem('authToken', 'super-admin-token');
      localStorage.setItem('userData', JSON.stringify({
        email: 'info@easylearn.net.in',
        role: 'super_admin',
        name: 'Super Admin'
      }));
      // Store email for heartbeat
      localStorage.setItem('sessionEmail', formData.email);

      if (formData.rememberMe) {
        localStorage.setItem('rememberMe', 'true');
        localStorage.setItem('userEmail', formData.email);
      }
      
      setIsLoading(false);
      window.dispatchEvent(new Event('authChange'));

      // Start heartbeat for super admin too
      startSessionHeartbeat(formData.email);

      // Register logout on tab close
      registerLogoutOnUnload(formData.email);

      navigate('/');
      return;
    }
    // ── Static GrowthOS Super Admin Login ─────────────────────────────────────
    if (formData.email === 'info@isfathena.com' && formData.password === '1') {
      console.log('GrowthOS Super Admin Login - Static Credentials');

      localStorage.setItem('authToken', 'growthos-super-admin-token');
      localStorage.setItem('userData', JSON.stringify({
        email: 'info@isfathena.com',
        role: 'growthos_super_admin',
        name: 'GrowthOS Super Admin'
      }));
      // Store email for heartbeat
      localStorage.setItem('sessionEmail', formData.email);

      if (formData.rememberMe) {
        localStorage.setItem('rememberMe', 'true');
        localStorage.setItem('userEmail', formData.email);
      }

      setIsLoading(false);
      window.dispatchEvent(new Event('authChange'));

      // Start heartbeat for super admin too
      startSessionHeartbeat(formData.email);

      // Register logout on tab close
      registerLogoutOnUnload(formData.email);

      navigate('/growthos-admin');
      return;
    }
    // ── Static GrowthOS Super Admin Login ─────────────────────────────────────
    if (formData.email === 'info@isfathena.com' && formData.password === '1') {
      console.log('GrowthOS Super Admin Login - Static Credentials');

      localStorage.setItem('authToken', 'growthos-super-admin-token');
      localStorage.setItem('userData', JSON.stringify({
        email: 'info@isfathena.com',
        role: 'growthos_super_admin',
        name: 'GrowthOS Super Admin'
      }));
      // Store email for heartbeat
      localStorage.setItem('sessionEmail', formData.email);

      if (formData.rememberMe) {
        localStorage.setItem('rememberMe', 'true');
        localStorage.setItem('userEmail', formData.email);
      }

      setIsLoading(false);
      window.dispatchEvent(new Event('authChange'));

      // Start heartbeat for super admin too
      startSessionHeartbeat(formData.email);

      // Register logout on tab close
      registerLogoutOnUnload(formData.email);

      navigate('/growthos-admin');
      return;
    }
    
    
    console.log('Login attempt with:', {
      email: formData.email,
      password: formData.password,
      rememberMe: formData.rememberMe
    });
    
    try {
      const response = await fetch(`${API_BASE_URL}/isfathena/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          email: formData.email,
          password: formData.password,
          rememberMe: formData.rememberMe
        })
      });

      const data = await response.json();
      console.log('API Response:', { status: response.status, data });

      if (data.success === true) {
        console.log('Login successful:', data);
        
        if (data.token) {
          localStorage.setItem('authToken', data.token);
        }
        if (data.user) {
          localStorage.setItem('userData', JSON.stringify(data.user));
        }

        // ★ Store email for heartbeat
        localStorage.setItem('sessionEmail', formData.email);
        
        if (formData.rememberMe) {
          localStorage.setItem('rememberMe', 'true');
          localStorage.setItem('userEmail', formData.email);
        }

        window.dispatchEvent(new Event('authChange'));

        // ★ Start sending heartbeat pings every 60 seconds
        startSessionHeartbeat(formData.email);

        // ★ Register logout when browser tab closes
        registerLogoutOnUnload(formData.email);

        navigate('/');
      } else {
        console.log('Login failed:', data);
        
        if (data.message) {
          setApiError(data.message);
        } else if (data.details && Array.isArray(data.details)) {
          const errorMessages = data.details.map(detail => detail.msg).join(', ');
          setApiError(errorMessages || 'Login failed. Please check your credentials.');
        } else {
          setApiError('Login failed. Please check your credentials.');
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      setApiError('Unable to connect to the server. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Register sendBeacon logout on browser/tab close ─────────────────────────
  function registerLogoutOnUnload(email) {
    // Remove any old listener first
    if (window._logoutHandler) {
      window.removeEventListener('beforeunload', window._logoutHandler);
    }
    window._logoutHandler = () => {
      // sendBeacon works even during page unload
      navigator.sendBeacon(
        `${API_BASE_URL}/isfathena/session/logout`,
        new URLSearchParams({ email })
      );
      if (window._sessionInterval) clearInterval(window._sessionInterval);
    };
    window.addEventListener('beforeunload', window._logoutHandler);
  }

  // ── Load remembered email on mount ──────────────────────────────────────────
  useEffect(() => {
    const rememberMe = localStorage.getItem('rememberMe');
    const savedEmail = localStorage.getItem('userEmail');
    if (rememberMe === 'true' && savedEmail) {
      setFormData(prev => ({
        ...prev,
        email: savedEmail,
        rememberMe: true
      }));
    }

    // ★ If user is already logged in (page refresh), resume heartbeat
    const sessionEmail = localStorage.getItem('sessionEmail');
    if (sessionEmail) {
      startSessionHeartbeat(sessionEmail);
      registerLogoutOnUnload(sessionEmail);
    }
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 relative overflow-hidden">
      
      {/* Animated stars background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(50)].map((_, i) => (
          <div
            key={i}
            className="absolute bg-white rounded-full"
            style={{
              width: Math.random() * 3 + 1 + 'px',
              height: Math.random() * 3 + 1 + 'px',
              top: Math.random() * 100 + '%',
              left: Math.random() * 100 + '%',
              animation: `twinkle ${Math.random() * 3 + 2}s infinite ${Math.random() * 2}s`,
            }}
          />
        ))}
      </div>

      {/* Background gradient blobs */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-orange-500 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-amber-600 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Twinkling animation */}
      <style>{`
        @keyframes twinkle {
          0%, 100% { opacity: 0.2; }
          50% { opacity: 1; }
        }
        
        img {
          pointer-events: none;
          user-select: none;
          -webkit-user-drag: none;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
        }

        /* Custom scrollbar */
        ::-webkit-scrollbar {
          width: 10px;
        }
        ::-webkit-scrollbar-track {
          background: #1a1a1a;
        }
        ::-webkit-scrollbar-thumb {
          background: #f97316;
          border-radius: 5px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: #ea580c;
        }
      `}</style>

      {/* Main Container */}
      <div className="relative z-10 flex items-center justify-center min-h-screen px-4 py-12">
        <div className="w-full max-w-md">
          
          {/* Login Card - Single Panel */}
          <div className="bg-white/95 backdrop-blur-md rounded-3xl p-8 sm:p-12 shadow-2xl">
            
            {/* Logo Section */}
            <div className="flex items-center space-x-3 mb-8">
              <img
                src={`${process.env.PUBLIC_URL}/Assests/isf_acedumy_logo.png`}
                alt="ISF Academy Logo"
                className="h-10 w-10 object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <h1 className="text-2xl font-bold text-gray-900">
                ISF<span className="text-orange-500"> Athena</span>
              </h1>
            </div>

            {/* Welcome Text */}
            <div className="mb-6">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
                Welcome Back
              </h2>
            </div>

            {/* Login Type Toggle */}
            <div className="mb-6 flex gap-2 rounded-xl bg-gray-100 p-1">
              <button
                type="button"
                onClick={() => { setLoginType('user'); setApiError(''); }}
                className={`flex-1 rounded-lg py-2.5 text-sm font-semibold transition-all duration-200 ${
                  loginType === 'user'
                    ? 'bg-white text-orange-600 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                User
              </button>
              <button
                type="button"
                onClick={() => { setLoginType('company'); setApiError(''); }}
                className={`flex-1 rounded-lg py-2.5 text-sm font-semibold transition-all duration-200 ${
                  loginType === 'company'
                    ? 'bg-white text-orange-600 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Company
              </button>
            </div>

            {/* API Error Message */}
            {apiError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">{apiError}</p>
              </div>
            )}

            {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* Email Field */}
              <div className="space-y-2">
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 bg-gray-50 border ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  } rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all duration-300`}
                  placeholder="Enter your email"
                />
                {errors.email && (
                  <p className="text-red-500 text-sm">{errors.email}</p>
                )}
              </div>

              {/* Password Field */}
              <div className="space-y-2">
                <label htmlFor="password" className="block text-sm font-semibold text-gray-700">
                  Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 bg-gray-50 border ${
                      errors.password ? 'border-red-500' : 'border-gray-300'
                    } rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all duration-300`}
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-500 hover:text-orange-500 transition-colors duration-200 focus:outline-none"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-red-500 text-sm">{errors.password}</p>
                )}
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between flex-wrap gap-2">
                <label className="flex items-center space-x-2 cursor-pointer group">
                  <input
                    type="checkbox"
                    name="rememberMe"
                    checked={formData.rememberMe}
                    onChange={handleInputChange}
                    className="w-4 h-4 rounded border-gray-300 bg-gray-50 text-orange-500 focus:ring-2 focus:ring-orange-500/50 cursor-pointer transition-all duration-200"
                  />
                  <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors duration-200">
                    Remember me
                  </span>
                </label>
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-sm text-orange-500 hover:text-orange-600 transition-colors duration-200 font-semibold"
                >
                  Forgot password?
                </button>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold text-base rounded-lg hover:from-orange-600 hover:to-orange-700 focus:outline-none focus:ring-4 focus:ring-orange-500/30 transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 shadow-lg shadow-orange-500/30 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:shadow-none flex items-center justify-center space-x-2"
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Logging in...</span>
                  </>
                ) : (
                  <>
                    <span>Log In</span>
                    {/* <ArrowRight className="w-5 h-5" /> */}
                  </>
                )}
              </button>
            </form>

            {/* Sign Up Link */}
            <div className="mt-8 text-center">
              <p className="text-gray-600 text-sm">
                Don't have an account?{' '}
                <button
                  onClick={() => navigate(loginType === 'company' ? '/company_register' : '/signup')}
                  className="text-orange-500 hover:text-orange-600 font-bold transition-colors duration-200"
                >
                  {loginType === 'company' ? 'Register your company' : 'Sign up'}
                </button>
              </p>
            </div>
          </div>

          {/* Footer Text */}
          <div className="mt-8 text-center px-4">
            <p className="text-gray-400 text-xs sm:text-sm leading-relaxed">
              By signing in, you agree to our{' '}
              <button onClick={() => navigate('/terms')} className="text-orange-500 hover:text-orange-400 transition-colors duration-200">
                Terms of Service
              </button>{' '}
              and{' '}
              <button onClick={() => navigate('/privacy')} className="text-orange-500 hover:text-orange-400 transition-colors duration-200">
                Privacy Policy
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;